import React, { useState, useEffect } from "react";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import {
  ChatMessage as ChatMessageType,
  ToolCall,
  ToolCallResponse,
} from "@/shared/contexts/gptgateway.context";
import MarkdownText from "@/shared/widgets/MarkdownText";
import { CustomTagsData } from "@/shared/widgets/MarkdownText/_components/MarkdownComponents";
import { cleanupMarkdownFromTags } from "@/shared/utils/parsers/cleanupMarkdownFromTags";

export interface ChatMessageComponentProps extends ChatMessageType {
  isStream?: boolean;
  disableCopy?: boolean;
  messageTagsData?: CustomTagsData;
}

// Helper to get text content from message
const getTextContent = (
  content: string | ToolCall | ToolCallResponse | null
): string => {
  if (typeof content === "string") {
    return content;
  }
  if (content && typeof content === "object") {
    if ("output" in content) {
      return content.output;
    }
    if ("name" in content && "arguments" in content) {
      return `Calling ${content.name}(${content.arguments})`;
    }
  }
  return "";
};

export const ChatMessage: React.FC<ChatMessageComponentProps> = (message) => {
  const [hasEditor, setHasEditor] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      // eslint-disable-next-line quotes
      const editor = document.querySelector('[data-slate-editor="true"]');
      setHasEditor(!!editor);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const handleCopyToClipboard = () => {
    // Get text content from message
    const content = getTextContent(message.content);

    try {
      if (hasEditor) {
        // Find the visible editor
        const editorContainers = document.querySelectorAll(
          // eslint-disable-next-line quotes
          '[data-slate-editor="true"]'
        );
        let visibleEditor = null;
        for (const editor of editorContainers) {
          const rect = editor.getBoundingClientRect();
          if (
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0
          ) {
            visibleEditor = editor;
            break;
          }
        }

        if (visibleEditor) {
          // Trigger the custom event with just the raw content
          const copyEvent = new CustomEvent("wisgpt-copy-to-editor", {
            bubbles: true,
            detail: { content },
          });
          visibleEditor.dispatchEvent(copyEvent);
          return; // Exit early if we successfully sent to editor
        }
      }

      // Fall back to clipboard if no visible editor found
      navigator.clipboard
        .writeText(cleanupMarkdownFromTags(content))
        .then(() => console.log("Message copied to clipboard"))
        .catch((err) => console.error("Failed to copy message: ", err));
    } catch (error) {
      console.error("Error copying to editor:", error);

      // Ultimate fallback - try clipboard copy if anything fails
      try {
        navigator.clipboard.writeText(content);
      } catch (clipboardError) {
        console.error("Clipboard fallback also failed:", clipboardError);
      }
    }
  };

  const textContent = getTextContent(message.content);

  return (
    <Row
      className={cn(
        `wisgpt-message-${message.role}`,
        "w-full relative",
        message.role === "user" && "justify-end"
      )}
      data-message={textContent}
    >
      <Column
        className={cn(
          "py-2 rounded-lg text-gray-700 prose text-md",
          message.role === "user" &&
            "px-2 rounded-br-none bg-purple-500 border border-solid border-purple-500 text-white prose-p:text-white",
          message.role === "assistant" && "rounded-tl-none",
          "prose text-left "
        )}
      >
        <MarkdownText
          data={message.messageTagsData}
          text={textContent}
          convertLegacyHtml={false}
        />
        {message.role === "assistant" &&
          !message?.disableCopy &&
          !message?.isStream && (
            <Row className="pt-4">
              <Tooltip>
                <TooltipTrigger>
                  <Icon
                    icon={hasEditor ? "SetAsNote" : "Copy"}
                    size="sm"
                    onClick={handleCopyToClipboard}
                    className="cursor-pointer text-grey-600"
                  />
                </TooltipTrigger>
                <TooltipContent side="right">
                  {hasEditor
                    ? "Copy-paste to note editor"
                    : "Copy to clipboard"}
                </TooltipContent>
              </Tooltip>
            </Row>
          )}
      </Column>
    </Row>
  );
};
